/**
 * 360° photos in the browser (1.173.0, #154): reading the camera's flag
 * before a photo is scaled down, and writing it again into the scaled one.
 *
 * **The server decides** whether a photo is shown as a sphere
 * (`Panorama::isFullSphere()`, and only for the trip's author). The browser
 * asks the same question for two reasons of its own: a sphere is scaled to
 * its own edge (`pano_max_edge`), not to the one of a flat photo, and the
 * redrawn JPEG would lose the XMP block that carries the flag -- without it
 * the server would see a flat photo. Two readers, one per language, held at
 * the same cases (`tools/pruefstand/panofaelle.json`); a change here is a
 * change there.
 */

import { APP1, XMP_HEADER, behindLeadingApps, encode, join, segment, xmpOf } from './jpegSegments'

/** How far from 2:1 a full sphere may be, as in `Panorama::ASPECT_SLACK`. */
const ASPECT_SLACK = 0.02


/** A GPano field, written as attribute or as element. */
function field(xmp: string, name: string): string | null {
  const attribute = new RegExp(`GPano:${name}\\s*=\\s*["']([^"']*)["']`).exec(xmp)
  if (attribute) return (attribute[1] ?? '').trim()
  const element = new RegExp(`<GPano:${name}>\\s*([^<]*?)\\s*</GPano:${name}>`).exec(xmp)
  return element ? (element[1] ?? '') : null
}

/** The same three conditions as `Panorama::isFullSphere()`. */
export function isFullSphere(jpeg: Uint8Array, width: number, height: number): boolean {
  if (height <= 0 || Math.abs(width / height - 2) > 2 * ASPECT_SLACK) return false
  const xmp = xmpOf(jpeg)
  if (xmp === null || (field(xmp, 'ProjectionType') ?? '').toLowerCase() !== 'equirectangular') return false
  for (const [part, full] of [
    ['CroppedAreaImageWidthPixels', 'FullPanoWidthPixels'],
    ['CroppedAreaImageHeightPixels', 'FullPanoHeightPixels'],
  ] as const) {
    const cropped = field(xmp, part)
    const whole = field(xmp, full)
    if (cropped !== null && whole !== null && Number(whole) > 0
      && Number(cropped) < Number(whole) * (1 - ASPECT_SLACK)) return false
  }
  return true
}

/**
 * The scaled photo with a fresh XMP block that says what it is.
 *
 * **Written new, not copied from the original.** The camera's block names its
 * own pixel sizes (`FullPanoWidthPixels="6080"`), and after scaling they would
 * be wrong; a viewer that trusts them would draw a partial sphere. The block
 * stands behind the EXIF block (`keepExif()` has put it there), where cameras
 * put it too.
 */
export function withSphereXmp(jpeg: Uint8Array, width: number, height: number): Uint8Array<ArrayBuffer> {
  const xmp = '<?xpacket begin="﻿" id="W5M0MpCehiHzreSzNTczkc9d"?>'
    + '<x:xmpmeta xmlns:x="adobe:ns:meta/"><rdf:RDF xmlns:rdf="http://www.w3.org/1999/02/22-rdf-syntax-ns#">'
    + '<rdf:Description rdf:about="" xmlns:GPano="http://ns.google.com/photos/1.0/panorama/"'
    + ' GPano:UsePanoramaViewer="True" GPano:ProjectionType="equirectangular"'
    + ` GPano:FullPanoWidthPixels="${width}" GPano:FullPanoHeightPixels="${height}"`
    + ` GPano:CroppedAreaImageWidthPixels="${width}" GPano:CroppedAreaImageHeightPixels="${height}"`
    + ' GPano:CroppedAreaLeftPixels="0" GPano:CroppedAreaTopPixels="0"/>'
    + '</rdf:RDF></x:xmpmeta><?xpacket end="w"?>'
  // Behind SOI and every APP0/APP1 at the front (JFIF, EXIF), with the
  // segment helpers every other JPEG writer here uses (1.187.0, C3).
  const at = behindLeadingApps(jpeg)
  return join([jpeg.subarray(0, at), segment(APP1, encode(XMP_HEADER + xmp)), jpeg.subarray(at)])
}
